import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, Settings2 } from 'lucide-react';
import { useAdminGameConfigs } from '../hooks';

const KEYWORD_RE = /^[A-Za-z0-9_-]{1,200}$/;

export function ConfigListPage() {
  const navigate = useNavigate();
  const { configs, loading, error, refresh } = useAdminGameConfigs();
  const [newKeyword, setNewKeyword] = useState('');

  const trimmed = newKeyword.trim();
  const keywordValid = KEYWORD_RE.test(trimmed);

  const handleCreate = () => {
    if (!keywordValid) return;
    // The detail page renders create mode when the keyword has no row yet.
    navigate(`/a/arche_dfe_game_state/admin/config/${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_dfe_game_state/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <h1 className="text-2xl font-semibold">Global Config</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Admin-curated, read-only configuration keyed by keyword (e.g.{' '}
          <span className="font-mono">litcraft_front_page</span>). Plugins read these via the{' '}
          <span className="font-mono">get_config</span> entity verb once an admin approves a{' '}
          <span className="font-mono">config</span> grant for the arche.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4">
          <label htmlFor="new-config-keyword" className="text-xs text-muted-foreground block mb-1">
            New config keyword
          </label>
          <div className="flex gap-2">
            <input
              id="new-config-keyword"
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              placeholder="e.g. litcraft_front_page"
              className="flex-1 px-3 py-2 border rounded-md bg-background text-sm font-mono"
            />
            <Button type="button" onClick={handleCreate} disabled={!keywordValid}>
              Create / Edit
            </Button>
          </div>
          {trimmed.length > 0 && !keywordValid && (
            <p className="text-xs text-destructive mt-1">
              Keyword must be 1–200 chars: letters, numbers, hyphens, underscores only.
            </p>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="py-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={refresh}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : configs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Settings2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No config keywords yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Create one above to publish admin-curated config to plugins.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-md divide-y">
          {configs.map((c) => (
            <Link
              key={c.keyword}
              to={`/a/arche_dfe_game_state/admin/config/${encodeURIComponent(c.keyword)}`}
              className="flex items-center justify-between p-4 hover:bg-muted/30"
            >
              <div>
                <p className="font-mono font-medium">{c.keyword}</p>
                {c.description && (
                  <p className="text-sm text-muted-foreground">{c.description}</p>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Updated {new Date(c.updatedAt).toLocaleString()}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
